import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isEqual, isNull, merge } from 'lodash-es';
import { DateTime } from 'luxon';
import { BehaviorSubject, debounceTime, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { amountMask, rateMask } from '../../../../../constants/maskito.constants';
import { checkFormValidity, showComponentByFieldsVisibility } from '../../../../../helpers/form';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import { NetSheetFormFieldSettings } from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';
import { DatePickerService } from '../../../../services/date-picker.service';

interface FormModel {
  mortgagePrimaryBalance: FormControl<string | null>;
  mortgagePrimaryInterestRate: FormControl<string | null>;
  mortgagePrimaryLienCloseDate: FormControl<string | null>;
  mortgagePrimaryPrepayPenalty: FormControl<string | null>;
  mortgageSecondaryBalance: FormControl<string | null>;
  mortgageSecondaryInterestRate: FormControl<string | null>;
  mortgageSecondaryPrepayPenalty: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: MortgageComponent
    }
  ],
  selector: 'red-net-sheet-mortgage',
  standalone: false,
  templateUrl: './mortgage.component.html'
})
export class MortgageComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);
  private datePickerService = inject(DatePickerService);

  showPrimaryComponent$ = new BehaviorSubject(true);
  showSecondaryComponent$ = new BehaviorSubject(true);

  form = new FormGroup<FormModel>({
    mortgagePrimaryBalance: new FormControl<string | null>(null),
    mortgagePrimaryInterestRate: new FormControl<string | null>(null),
    mortgagePrimaryLienCloseDate: new FormControl<string | null>(null),
    mortgagePrimaryPrepayPenalty: new FormControl<string | null>(null),
    mortgageSecondaryBalance: new FormControl<string | null>(null),
    mortgageSecondaryInterestRate: new FormControl<string | null>(null),
    mortgageSecondaryPrepayPenalty: new FormControl<string | null>(null)
  });

  fieldsSettings$ = new BehaviorSubject<{ [key: string]: NetSheetFormFieldSettings } | null>(null);

  constructor() {
    super();

    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) => isEqual(previous, current))
      )
      .subscribe((values) => {
        this.store.dispatch(
          fromNetSheetActions.updateMortgage({
            payload: values
          })
        );
      });
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectMortgageFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(
            {
              ...data
            },
            { emitEvent: false }
          );
        })
    );

    this.addUniqueSubscription(
      'fieldsSettingsSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectMortgageFieldsSettings),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe(({ primary, secondary }) => {
          this.showPrimaryComponent$.next(showComponentByFieldsVisibility(primary));
          this.showSecondaryComponent$.next(showComponentByFieldsVisibility(secondary));
          const fields = merge(primary, secondary);
          this.fieldsSettings$.next(fields);
        })
    );
  }

  async onDatePickerInputClick(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    const date = await this.datePickerService.open(this.form.controls.mortgagePrimaryLienCloseDate.value ?? null, {
      max: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd'T'00:00:00")
    });

    if (!isNull(date)) {
      this.form.patchValue({
        mortgagePrimaryLienCloseDate: date
      });
    }
  }

  readonly rateMask: MaskitoOptions = rateMask;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly valid = checkFormValidity(this.form);
}
